import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  type ReactNode,
} from 'react';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type Theme = 'light' | 'dark' | 'system';
export type ResolvedTheme = 'light' | 'dark';

interface ThemeContextValue {
  /** The user's stored preference: "light", "dark", or "system". */
  theme: Theme;
  /** The concrete theme actually applied to the document ("light" | "dark"). */
  resolvedTheme: ResolvedTheme;
  /** The OS-level preference ("light" | "dark"), independent of `theme`. */
  systemTheme: ResolvedTheme;
  /** Update the stored preference (persists to localStorage + applies immediately). */
  setTheme: (theme: Theme) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STORAGE_KEY = 'nmblr-docs-theme';
const DEFAULT_THEME: Theme = 'system';
const FALLBACK_RESOLVED: ResolvedTheme = 'dark';
const VALID_THEMES: readonly Theme[] = ['light', 'dark', 'system'];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isValidTheme(value: string | null): value is Theme {
  return value !== null && (VALID_THEMES as readonly string[]).includes(value);
}

/**
 * Read the stored preference. Distinguishes three cases:
 *   - absent (no key)         → DEFAULT_THEME ("system")
 *   - present + valid         → the stored value
 *   - present + invalid/error → "dark" (whitelist / error fallback)
 */
function readStoredTheme(): Theme {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored === null) return DEFAULT_THEME; // never set → system default
    if (isValidTheme(stored)) return stored;
    return FALLBACK_RESOLVED; // present but not whitelisted → dark
  } catch {
    return FALLBACK_RESOLVED;
  }
}

/** Read the OS preference via matchMedia; any error → "dark". */
function readSystemTheme(): ResolvedTheme {
  try {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'dark'
      : 'light';
  } catch {
    return FALLBACK_RESOLVED;
  }
}

/** Resolve a stored preference + system theme into a concrete theme. */
function resolveTheme(theme: Theme, systemTheme: ResolvedTheme): ResolvedTheme {
  return theme === 'system' ? systemTheme : theme;
}

/**
 * Apply the resolved theme to <html>. Sets ONLY the data-theme attribute —
 * never style.colorScheme (CSS `var(--color-scheme)` is the single source of
 * truth for native form-control/scrollbar styling).
 */
function applyTheme(resolved: ResolvedTheme): void {
  try {
    document.documentElement.setAttribute('data-theme', resolved);
  } catch {
    // No-op: document unavailable (defensive; CSR-only SPA).
  }
}

// ---------------------------------------------------------------------------
// Context
// ---------------------------------------------------------------------------

const ThemeContext = createContext<ThemeContextValue | null>(null);

// ---------------------------------------------------------------------------
// Provider
// ---------------------------------------------------------------------------

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<Theme>(() => readStoredTheme());
  const [systemTheme, setSystemTheme] = useState<ResolvedTheme>(() =>
    readSystemTheme(),
  );

  const resolvedTheme = resolveTheme(theme, systemTheme);

  // Apply the resolved theme to <html> whenever it changes.
  useEffect(() => {
    applyTheme(resolvedTheme);
  }, [resolvedTheme]);

  // Listen for OS preference changes. Only affects the applied theme when the
  // stored preference is "system" (resolveTheme handles that); we always keep
  // systemTheme current so consumers can read it.
  useEffect(() => {
    let mql: MediaQueryList;
    try {
      mql = window.matchMedia('(prefers-color-scheme: dark)');
    } catch {
      return;
    }

    const handleChange = (event: MediaQueryListEvent) => {
      setSystemTheme(event.matches ? 'dark' : 'light');
    };

    // Modern browsers: addEventListener. Legacy (Safari < 14): addListener.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', handleChange);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(handleChange);
    }

    return () => {
      if (typeof mql.removeEventListener === 'function') {
        mql.removeEventListener('change', handleChange);
      } else if (typeof mql.removeListener === 'function') {
        mql.removeListener(handleChange);
      }
    };
  }, []);

  // Cross-tab sync: when another tab writes the preference, mirror it here.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.key !== STORAGE_KEY) return;
      const next = event.newValue;
      setThemeState(isValidTheme(next) ? next : FALLBACK_RESOLVED);
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // Update the stored preference; persists and applies immediately.
  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    try {
      localStorage.setItem(STORAGE_KEY, next);
    } catch {
      // Persistence failed (e.g. private mode quota) — state still updates.
    }
  }, []);

  return (
    <ThemeContext.Provider
      value={{ theme, resolvedTheme, systemTheme, setTheme }}
    >
      {children}
    </ThemeContext.Provider>
  );
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

/**
 * Access the theme context. Returns SAFE DEFAULTS when called outside a
 * ThemeProvider (no throw).
 */
export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (ctx) return ctx;

  return {
    theme: DEFAULT_THEME,
    resolvedTheme: FALLBACK_RESOLVED,
    systemTheme: FALLBACK_RESOLVED,
    setTheme: () => {},
  };
}
